import { useMemo } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useFormContext } from 'react-hook-form';
import { DialogProps } from '@radix-ui/react-dialog';

import { OrderType } from '@/types/enums';
import { useAccountStore } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { useSettingsStore } from '@/store/use-settings-store';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui';
import {
  getIsStatusEval,
  getIsStatusFundedOrEval,
} from '@/features/vest-capital/utils/status';
import { formatBigInt, parseDecimalToBigInt } from '@/utils/value-format';

import { OrderFormSchema } from './order-form';
import {
  calculateSlippageAmount,
  getEstFillPrice,
  getBboPrice,
  getLimitPriceFromMaxSlippage,
  getSlippage,
  getSlippageReferencePrice,
} from '../utils/math';
import { getProjectedMidSlippageAmount } from '../utils/slippage-estimation';

const ORDER_CONFIRM_SLIPPAGE_WARNING_BPS = 1000n; // 10 bps

export const getShouldConfirm = ({ order }: OrderFormSchema) => {
  if (order.orderType !== OrderType.MARKET) {
    return false;
  }

  const { showOrderConfirm } = useSettingsStore.getState();

  if (!showOrderConfirm) {
    return false;
  }

  const { book, marketSpec } = useMarketStore(order.symbol).getState();
  const { address, getCapitalStatus } = useAccountStore.getState();
  const isEval = getIsStatusEval(getCapitalStatus(address));

  const size = parseDecimalToBigInt(order.size, marketSpec.sizeDecimals);
  const estFillPrice = getEstFillPrice(
    order.isBuy,
    size,
    book,
    marketSpec.priceDecimals,
    isEval,
  );

  if (!estFillPrice) {
    return false;
  }

  if (estFillPrice.bigint === null || estFillPrice.decimal === null) {
    return true;
  }

  const slippage = getSlippage(
    order.symbol,
    order.isBuy,
    estFillPrice.bigint,
    isEval
      ? undefined
      : {
          size,
          visibleCost: estFillPrice.visibleCost,
          visibleFilled: estFillPrice.visibleFilled,
          book,
        },
  )?.bigint;

  // Non-eval slippage has no projected post-order midpoint when the visible
  // book cannot absorb the order or nothing remains after it; treat a missing
  // estimate as high slippage instead of silently skipping the confirmation.
  if (!isEval && slippage === undefined) {
    return true;
  }

  if (slippage !== undefined && slippage > ORDER_CONFIRM_SLIPPAGE_WARNING_BPS) {
    return true;
  }

  const exceedsMaxSlippage = order.isBuy
    ? +estFillPrice.decimal >= +order.limitPrice
    : +estFillPrice.decimal <= +order.limitPrice;

  return exceedsMaxSlippage;
};

interface OrderConfirmProps extends DialogProps {
  onSubmit: () => void;
}

const OrderConfirm = (props: OrderConfirmProps) => {
  const { open, onSubmit, onOpenChange } = props;

  const form = useFormContext<OrderFormSchema>();
  const symbol = form.watch('order.symbol');
  const isBuy = form.watch('order.isBuy');

  const { book, marketSpec, midPrice } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      book: state.book,
      marketSpec: state.marketSpec,
      midPrice: state.marketData.midPrice,
    })),
  );
  const { sizeDecimals, priceDecimals } = marketSpec;
  const { isEval, isFundedOrEval } = useAccountStore(
    useShallow((state) => {
      const capitalStatus = state.getCapitalStatus(state.address);
      return {
        isEval: getIsStatusEval(capitalStatus),
        isFundedOrEval: getIsStatusFundedOrEval(capitalStatus),
      };
    }),
  );

  const size = parseDecimalToBigInt(form.watch('order.size'), sizeDecimals);
  const limitPriceBigInt = parseDecimalToBigInt(
    form.watch('order.limitPrice'),
    priceDecimals,
  );
  const slippageReferencePrice = useStore(useMarketStore(symbol), () =>
    getBboPrice(symbol, isBuy),
  );

  const { estFillPrice, slippage } = useMemo(() => {
    const estFillPriceResult = getEstFillPrice(
      isBuy,
      size,
      book,
      priceDecimals,
      isEval,
    );

    if (!estFillPriceResult) {
      return {
        estFillPrice: undefined,
        slippage: undefined,
      };
    }

    const slippageResult =
      estFillPriceResult.bigint === null
        ? undefined
        : getSlippage(
            symbol,
            isBuy,
            estFillPriceResult.bigint,
            isEval
              ? undefined
              : {
                  size,
                  visibleCost: estFillPriceResult.visibleCost,
                  visibleFilled: estFillPriceResult.visibleFilled,
                  book,
                },
          );

    return {
      estFillPrice: estFillPriceResult,
      slippage: slippageResult,
    };
  }, [symbol, isBuy, size, book, priceDecimals, isEval]);

  const slippageAmount = useMemo(
    () =>
      isEval
        ? calculateSlippageAmount(isBuy, size, slippageReferencePrice, midPrice)
        : getProjectedMidSlippageAmount(
            isBuy,
            size,
            estFillPrice?.visibleCost ?? 0n,
            estFillPrice?.visibleFilled ?? 0n,
            book,
          ),
    [book, estFillPrice, isBuy, isEval, midPrice, size, slippageReferencePrice],
  );

  const formattedSlippage = isFundedOrEval
    ? slippageAmount === null
      ? '—'
      : formatBigInt(slippageAmount, priceDecimals + sizeDecimals, {
          digits: 2,
          style: 'currency',
        })
    : slippage
      ? `${slippage.decimal}%`
      : '—';

  const { needsSlippageIncrease, suggestedMaxSlippagePct } = useMemo(() => {
    if (!estFillPrice?.bigint || !slippageReferencePrice) {
      return { needsSlippageIncrease: false, suggestedMaxSlippagePct: 0n };
    }

    const requiredMaxSlippagePct =
      ((isBuy ? 1n : -1n) *
        (estFillPrice.bigint - slippageReferencePrice) *
        10000n) /
      slippageReferencePrice;
    const suggested =
      (requiredMaxSlippagePct > 0n ? requiredMaxSlippagePct : 0n) + 10n; // 10 bps
    const currentMaxSlippagePct =
      ((isBuy ? 1n : -1n) *
        (limitPriceBigInt - slippageReferencePrice) *
        10000n) /
      slippageReferencePrice;

    return {
      needsSlippageIncrease: currentMaxSlippagePct < suggested,
      suggestedMaxSlippagePct: suggested,
    };
  }, [estFillPrice, slippageReferencePrice, isBuy, limitPriceBigInt]);

  const handleSubmitWithIncreasedSlippage = () => {
    const referencePrice = getSlippageReferencePrice(symbol, isBuy);
    if (!estFillPrice?.bigint || !referencePrice) {
      return;
    }

    const newLimitPrice = getLimitPriceFromMaxSlippage(
      isBuy,
      suggestedMaxSlippagePct,
      referencePrice,
      priceDecimals,
    ).decimal;

    form.setValue('order.limitPrice', newLimitPrice);
    onSubmit();
  };

  const { isSubmitting } = form.formState;
  const isEstimateUnavailable =
    !estFillPrice?.bigint || !slippageReferencePrice;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="sm:max-w-md">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="text-xl text-foreground">
            High Slippage Warning
          </DialogTitle>

          <DialogDescription>
            {isEstimateUnavailable
              ? 'Current BBO or liquidity data is unavailable. Please wait for the order book to update before submitting this order.'
              : 'This order has higher slippage than expected. You can submit as-is, increase max slippage, or cancel.'}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5">
          <div className="flex h-8 items-center justify-between">
            <p className="flex items-center gap-2">Size</p>

            <span className="leading-tight text-foreground">
              {formatBigInt(size, sizeDecimals)}
            </span>
          </div>

          {estFillPrice?.bigint !== null &&
            estFillPrice?.bigint !== undefined && (
              <div className="flex h-8 items-center justify-between">
                <p className="flex items-center gap-2">Fill Price</p>

                <span className="leading-tight text-foreground">
                  {formatBigInt(estFillPrice.bigint, priceDecimals, {
                    digits: Number(priceDecimals),
                  })}
                </span>
              </div>
            )}

          <div className="flex h-8 items-center justify-between">
            <p className="flex items-center gap-2">Slippage</p>

            <span className="text-yellow-500 leading-tight">
              {formattedSlippage}
            </span>
          </div>

          <div className="mt-2 flex h-10 items-center gap-3">
            <Checkbox
              id="order-confirm"
              className="mt-0.5 size-5"
              onCheckedChange={(checked) =>
                useSettingsStore.setState({ showOrderConfirm: !checked })
              }
            />

            <Label
              htmlFor="order-confirm"
              className="cursor-pointer text-base leading-none text-vestgrey-600"
            >
              Don't show this again
            </Label>
          </div>
        </div>

        <DialogFooter className="grid pt-2 sm:justify-normal">
          {needsSlippageIncrease ? (
            <>
              <DialogClose asChild>
                <Button
                  type="button"
                  size="xl"
                  className="w-full rounded-none uppercase tracking-wider"
                  disabled={isEstimateUnavailable}
                  loading={isSubmitting}
                  onClick={handleSubmitWithIncreasedSlippage}
                >
                  Submit with Increased Slippage
                </Button>
              </DialogClose>

              <DialogClose asChild>
                <Button
                  type="button"
                  size="xl"
                  variant="ghost"
                  className="w-full rounded-none uppercase tracking-wider"
                  disabled={isEstimateUnavailable || limitPriceBigInt <= 0n}
                  loading={isSubmitting}
                  onClick={onSubmit}
                >
                  Submit Without Changes
                </Button>
              </DialogClose>
            </>
          ) : (
            <DialogClose asChild>
              <Button
                type="button"
                size="xl"
                className="w-full rounded-none uppercase tracking-wider"
                disabled={isEstimateUnavailable || limitPriceBigInt <= 0n}
                loading={isSubmitting}
                onClick={onSubmit}
              >
                Submit Order
              </Button>
            </DialogClose>
          )}

          <DialogClose asChild>
            <Button
              type="button"
              size="xl"
              variant="ghost"
              className="w-full rounded-none uppercase tracking-wider"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OrderConfirm;
